import { Routes } from '@angular/router';
import {
  authGuard,
  AuthState,
  permissionGuard,
} from '@ipg/ngx-frontend-toolset';
import { MainLayoutComponent } from './components/layout/main-layout/main-layout.component';
import { canActivateClientGuard } from './guards/can-activate-client.guard';
import { canActivateNonRootUnitGuard } from './guards/can-activate-non-root-unit.guard';
import { canActivateRootUnitGuard } from './guards/can-activate-root-unit.guard';
import {
  anyPermissionGuard,
  defaultRedirectGuard,
  settingsRedirectGuard,
} from './guards/permission.guard';
import { AuditLogsComponent } from './views/audit-logs/audit-logs.component';
import { LoginComponent } from './views/auth/login/login.component';
import { ResetPasswordComponent } from './views/auth/reset-password/reset-password.component';
import { SupportLoginComponent } from './views/auth/support-login/support-login.component';
import { ClientDevicesComponent } from './views/client-devices/client-devices.component';
import { ClientInteractionsComponent } from './views/client-interactions/client-interactions.component';
import { ClientSettingsComponent } from './views/client-settings/client-settings.component';
import { ClientSignalingComponent } from './views/client-signaling/client-signaling.component';
import { ClientStatisticsComponent } from './views/client-statistics/client-statistics.component';
import { ClientWorkspaceComponent } from './views/client-workspace/client-workspace.component';
import { ClientsComponent } from './views/clients/clients.component';
import { MoveClientWizardComponent } from './views/clients/components/move-client-wizard/move-client-wizard.component';
import { DevicesLayoutComponent } from './views/devices/devices-layout.component';
import { DevicesRobotsComponent } from './views/devices/devices-robots.component';
import { DevicesAccessPointsComponent } from './views/devices/devices-access-points.component';
import { DownloadsComponent } from './views/downloads/downloads.component';
import { InteractionCreatorComponent } from './views/interaction-creator/interaction-creator.component';
import { InteractionEditorComponent } from './views/interaction-editor/interaction-editor.component';
import { InteractionFlowComponent } from './views/interaction-flow/interaction-flow.component';
import { InteractionHistoryComponent } from './views/interaction-history/interaction-history.component';
import { LibErrorReportComponent } from './views/lib-error-report/lib-error-report.component';
import { MediaComponent } from './views/media/media.component';
import { MoveTemplateWizardComponent } from './views/client-interactions/components/move-template-wizard/move-template-wizard.component';
import { NoAccessComponent } from './views/no-access/no-access.component';
import { NotificationLogsComponent } from './views/notification-logs/notification-logs.component';
import { RobotSessionsComponent } from './views/robot-sessions/robot-sessions.component';
import { SchedulerComponent } from './views/scheduler/scheduler.component';
import { SettingsContactsComponent } from './views/settings-contacts/settings-contacts.component';
import { SettingsBillingComponent } from './views/settings-billing/settings-billing.component';
import { SettingsGeneralComponent } from './views/settings-general/settings-general.component';
import { PermissionsMatrixComponent } from './views/settings-roles/permissions-matrix/permissions-matrix.component';
import { RoleDetailComponent } from './views/settings-roles/role-detail/role-detail.component';
import { SettingsRolesComponent } from './views/settings-roles/settings-roles.component';
import { SettingsSupportSessionsComponent } from './views/settings-support-sessions/settings-support-sessions.component';
import { SettingsTagsComponent } from './views/settings-tags/settings-tags.component';
import { SettingsVariablesComponent } from './views/settings-variables/settings-variables.component';
import { SettingsAiAgentsComponent } from './views/settings-ai-agents/settings-ai-agents.component';
import { SettingsActionsMoveUnitComponent } from './views/settings-actions-move-unit/settings-actions-move-unit.component';
import { SettingsActionsMoveUserComponent } from './views/settings-actions-move-user/settings-actions-move-user.component';
import { SettingsComponent } from './views/settings/settings.component';
import { TenantEntryComponent } from './views/tenant-entry/tenant-entry.component';
import { TenantNotFoundComponent } from './views/tenant-not-found/tenant-not-found.component';
import { UnitsComponent } from './views/units/units.component';
import { UserDetailComponent } from './views/users/user-detail/user-detail.component';
import { UsersComponent } from './views/users/users.component';
import { DocsComponent } from './views/docs/docs.component';


import { onboardingGuard } from './guards/onboarding.guard';
import { OnboardingWizardComponent } from './views/onboarding-wizard/onboarding-wizard.component';

export const routes: Routes = [
  // Standalone tenant entry routes (no APP_BASE_HREF)
  {
    path: 'enter-organization',
    component: TenantEntryComponent,
    title: 'Organisatie invoeren',
  },
  {
    path: 'organization-not-found',
    component: TenantNotFoundComponent,
    title: 'Organisatie niet gevonden',
  },
  {
    path: 'error-report',
    component: LibErrorReportComponent,
    title: 'Raport error',
  },
  {
    path: '',
    component: MainLayoutComponent,
    canActivate: [authGuard(AuthState.Authorized)],
    // `canActivateChild`, not `canActivate`: a parent's `canActivate` does not
    // re-run when navigating between its own children, which let users click
    // straight out of the onboarding wizard.
    canActivateChild: [onboardingGuard],
    children: [
      {
        path: 'onboarding',
        component: OnboardingWizardComponent,
        title: 'Inrichting Omgeving',
      },
      {
        path: '',
        pathMatch: 'full',
        component: ClientsComponent,
        canActivate: [defaultRedirectGuard],
      },
      {
        path: 'clients',
        component: ClientsComponent,
        title: 'Cliënten',
        canActivate: [permissionGuard('client:read')],
      },
      {
        path: 'clients/:clientId/move',
        component: MoveClientWizardComponent,
        title: 'Cliënt verplaatsen',
        canActivate: [permissionGuard('client:move')],
      },
      {
        path: 'clients/:clientId',
        component: ClientWorkspaceComponent,
        canActivate: [permissionGuard('client:read'), canActivateClientGuard],
        title: 'Workspace',
        children: [
          {
            path: '',
            redirectTo: 'overview',
            pathMatch: 'full',
          },
          {
            path: 'overview',
            component: ClientStatisticsComponent,
            title: 'Overzicht',
          },
          {
            path: 'devices',
            component: ClientDevicesComponent,
            title: 'Apparaten',
            canActivate: [permissionGuard('device:read')],
          },
          {
            path: 'interactions',
            component: ClientInteractionsComponent,
            title: 'Interacties',
            canActivate: [permissionGuard('interaction:read')],
          },
          {
            path: 'interactions/new',
            component: InteractionCreatorComponent,
            title: 'Nieuwe interactie',
            canActivate: [permissionGuard('interaction:create')],
          },
          {
            path: 'history',
            component: InteractionHistoryComponent,
            title: 'Interactie geschiedenis',
          },
          {
            path: 'signaling',
            component: ClientSignalingComponent,
            title: 'Signalering',
            canActivate: [permissionGuard('scm:read')],
          },
          {
            path: 'scheduler',
            component: SchedulerComponent,
            title: 'Planning',
            canActivate: [permissionGuard('client_event:read')],
          },
          {
            path: 'settings',
            component: ClientSettingsComponent,
            title: 'Instellingen',
          },
          {
            path: 'media',
            component: MediaComponent,
            title: 'Media',
            canActivate: [permissionGuard('media:read')],
            data: { scope: 'client' },
          },
        ],
      },
      {
        path: 'interactions/:interactionId',
        component: InteractionEditorComponent,
        title: 'Interactie bewerken',
        canActivate: [permissionGuard('interaction:read')],
      },
      {
        path: 'interactions/:interactionId/flow',
        component: InteractionFlowComponent,
        title: 'Interactie flow',
        canActivate: [permissionGuard('interaction:read')],
      },
      {
        path: 'templates',
        component: ClientInteractionsComponent,
        title: 'Templates',
        canActivate: [permissionGuard('interaction:read')],
      },
      {
        path: 'templates/new',
        component: InteractionCreatorComponent,
        title: 'Nieuwe template',
        canActivate: [permissionGuard('interaction:create')],
      },
      {
        path: 'templates/:interactionId',
        component: InteractionEditorComponent,
        title: 'Template bewerken',
        canActivate: [permissionGuard('interaction:read')],
      },
      {
        path: 'templates/:interactionId/flow',
        component: InteractionFlowComponent,
        title: 'Template flow',
        canActivate: [permissionGuard('interaction:read')],
      },
      {
        path: 'templates/:correlationId/move',
        component: MoveTemplateWizardComponent,
        title: 'Template verplaatsen',
        canActivate: [permissionGuard('interaction:move')],
      },
      {
        path: 'devices',
        component: DevicesLayoutComponent,
        title: 'Apparaten',
        canActivate: [permissionGuard('device:read')],
        children: [
          { path: '', redirectTo: 'robots', pathMatch: 'full' },
          { path: 'robots', component: DevicesRobotsComponent, title: 'Robots' },
          {
            path: 'access-points',
            component: DevicesAccessPointsComponent,
            title: 'Access points',
          },
        ],
      },
      {
        path: 'robots/:robotId/sessions',
        component: RobotSessionsComponent,
        title: 'Robots',
        canActivate: [permissionGuard('device:read')],
      },
      {
        path: 'media',
        component: MediaComponent,
        title: 'Algemene media',
        canActivate: [permissionGuard('media:read')],
      },
      {
        path: 'downloads',
        component: DownloadsComponent,
        title: 'Downloads',
        canActivate: [permissionGuard('download:read')],
      },
      {
        path: 'settings',
        component: SettingsComponent,
        title: 'Instellingen',
        children: [
          {
            path: '',
            pathMatch: 'full',
            canActivate: [settingsRedirectGuard],
            children: [],
          },
          {
            path: 'general',
            component: SettingsGeneralComponent,
            title: 'Algemene instellingen',
            canActivate: [permissionGuard('unit:read')],
          },
          {
            path: 'billing',
            component: SettingsBillingComponent,
            title: 'Facturatie',
            canActivate: [permissionGuard('unit:read')],
          },
          {
            path: 'users',
            canActivate: [permissionGuard('user:read')],
            children: [
              {
                path: '',
                component: UsersComponent,
                title: 'Gebruikers',
              },
              {
                path: 'new',
                component: UserDetailComponent,
                title: 'Nieuwe gebruiker',
                canActivate: [permissionGuard('user:create')],
              },
              {
                path: ':userId',
                component: UserDetailComponent,
                title: 'Gebruiker bewerken',
                canActivate: [permissionGuard('user:update')],
              },
              {
                path: ':userId/move',
                component: SettingsActionsMoveUserComponent,
                title: 'Gebruiker verplaatsen',
                canActivate: [permissionGuard('user:move')],
              },
            ],
          },
          {
            path: 'roles',
            canActivate: [permissionGuard('user_role:read')],
            children: [
              {
                path: '',
                component: SettingsRolesComponent,
                title: 'Rollen',
              },
              {
                path: 'matrix',
                component: PermissionsMatrixComponent,
                title: 'Rechten matrix',
              },
              {
                path: 'new',
                component: RoleDetailComponent,
                title: 'Nieuwe rol',
                canActivate: [
                  permissionGuard('user_role:create'),
                  canActivateRootUnitGuard,
                ],
              },
              {
                path: ':roleId',
                component: RoleDetailComponent,
                title: 'Rol bewerken',
                canActivate: [
                  permissionGuard('user_role:update'),
                  canActivateRootUnitGuard,
                ],
              },
            ],
          },
          {
            path: 'contacts',
            component: SettingsContactsComponent,
            title: 'Contacten',
            canActivate: [permissionGuard('contact:read')],
          },
          {
            path: 'variables',
            component: SettingsVariablesComponent,
            title: 'Variabelen',
            canActivate: [permissionGuard('var:read'), canActivateRootUnitGuard],
          },
          {
            path: 'tags',
            component: SettingsTagsComponent,
            title: 'Tags',
            canActivate: [permissionGuard('tag:read'), canActivateRootUnitGuard],
          },
          {
            path: 'support-sessions',
            component: SettingsSupportSessionsComponent,
            title: 'Supporttoegang',
            canActivate: [permissionGuard('support_session:read')],
          },
          {
            path: 'ai-agents/creator',
            component: SettingsAiAgentsComponent,
            data: { agent: 'interaction_creator' },
            title: 'Interactie-maker',
            canActivate: [permissionGuard('ai_agent_settings:read'), canActivateRootUnitGuard],
          },
          {
            path: 'ai-agents/auditor',
            component: SettingsAiAgentsComponent,
            data: { agent: 'interaction_auditor' },
            title: 'Interactie-auditor',
            canActivate: [permissionGuard('ai_agent_settings:read'), canActivateRootUnitGuard],
          },
          {
            path: 'ai-agents',
            redirectTo: 'ai-agents/creator',
            pathMatch: 'full',
          },
          {
            path: 'notification-logs',
            component: NotificationLogsComponent,
            title: 'Notificatielogs',
            canActivate: [
              permissionGuard('notification_log:read'),
              canActivateRootUnitGuard,
            ],
          },
          {
            path: 'audit-logs',
            component: AuditLogsComponent,
            title: 'Audits',
            canActivate: [permissionGuard('audit_log:read'), canActivateRootUnitGuard],
          },
          {
            path: 'move',
            component: SettingsActionsMoveUnitComponent,
            title: 'Unit verplaatsen',
            canActivate: [permissionGuard('unit:move'), canActivateNonRootUnitGuard],
          },
        ],
      },
    ],
  },
  {
    path: 'units',
    component: UnitsComponent,
    title: 'Units',
    // Sits outside the shell route, so it needs `onboardingGuard` of its own —
    // otherwise /units is a free escape hatch out of the wizard.
    canActivate: [
      authGuard(AuthState.Authorized),
      onboardingGuard,
      permissionGuard('unit:read'),
    ],
  },
  {
    // Deliberately outside the shell route and free of any permissionGuard:
    // this is where `defaultRedirectGuard` sends a user who has nothing, so a
    // guard here would bounce them back to the root and into a redirect loop.
    path: 'no-access',
    component: NoAccessComponent,
    title: 'Geen toegang',
    canActivate: [authGuard(AuthState.Authorized)],
  },
  {
    path: 'docs',
    redirectTo: 'docs/introductie',
    pathMatch: 'full',
  },
  {
    path: 'docs/:slug',
    component: DocsComponent,
    title: 'Handleiding',
    // Also outside the shell route — see /units above. The manual describes
    // pages, so a user with no permission to open any of them has nothing to
    // read here; `anyPermissionGuard` keeps it from being the one page a
    // roleless account can still reach.
    canActivate: [
      authGuard(AuthState.Authorized),
      onboardingGuard,
      anyPermissionGuard,
    ],
  },
  {
    path: 'auth/support-login',
    component: SupportLoginComponent,
    title: 'Support Toegang',
  },
  {
    path: 'login',

    component: LoginComponent,
    title: 'Inloggen',
    canActivate: [authGuard(AuthState.Unauthorized)],
  },
  {
    path: 'reset-password/:resetToken',
    component: ResetPasswordComponent,
    title: 'Wachtwoord resetten',
  },
];
